import axios from "axios";

import {Campaign, ICampaign} from "@/shared/model/campaign.model";
import {DateTime} from "luxon";
import {IFullCampaign} from "@/shared/model/full-campaign.model";
import DataService from "@/core/base/service/data.service";
import {AlertStore} from "@/shared/config/store/alert.store";
import {getModule} from "vuex-module-decorators";
import {store} from "@/store";
import type {Confirmation} from "@/core/alert/interface/confirmation.interface";
import {AlertType} from "@/core/alert/enum/alert-type.enum";

export default class CampaignService extends DataService<Campaign> {
    protected baseApiUrl: string = "api/campaigns";

    private alertStore: AlertStore = getModule(AlertStore);

    public findFull(id: number): Promise<IFullCampaign> {
        return new Promise<ICampaign>((resolve, reject) => {
            axios
                .get(`${this.baseApiUrl}/${id}/full`)
                .then((res) => {
                    resolve(res.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    public async setReleaseCampaign(
        campaign: ICampaign,
        released: boolean,
    ): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (released) {
                this.alertStore.aShowCampaignReleaseDialog(campaign);
            } else {
                this.alertStore.aConfirmDialog({
                    key: "LOCK",
                    id: campaign.id,
                    title: "campaign.dialog_confirm_lock.title",
                    text: {
                        i18nKey: "campaign.dialog_confirm_lock.text",
                        parameters: {
                            campaign: campaign.name,
                        },
                    },
                    type: AlertType.WARNING,
                    confirmButtonLabel: "defaults.lock",
                    confirmButtonColor: "error",
                });

                const unwatch = store.watch(
                    () => this.alertStore.confirmed,
                    async (confirmation: Confirmation | null): Promise<void> => {
                        if (
                            confirmation &&
                            confirmation.confirmId &&
                            confirmation.confirmed
                        ) {
                            if (confirmation.confirmKey == "LOCK") {
                                try {
                                    await axios.put(`${this.baseApiUrl}/${campaign.id}/lock`);
                                    campaign.released = false;
                                    resolve();
                                } catch (e) {
                                    reject();
                                } finally {
                                    unwatch();
                                }
                            }
                        }
                    },
                );
            }
        });
    }

    public async releaseCampaign(
        campaign: ICampaign,
        sendMail: boolean,
    ): Promise<ICampaign> {
        try {
            const response = await axios.put(
                `${this.baseApiUrl}/${campaign.id}/release`,
                {
                    sendMail,
                },
            );
            if (response) {
                campaign.released = true;
            }
        } catch (err) {
            this.errorStore.handleApiError(err);
            return null;
        }
        return campaign;
    }

    public async setImportant(
        campaign: ICampaign,
        important: boolean,
    ): Promise<ICampaign> {
        try {
            const endpoint = important ? "pin" : "unpin";
            const response = await axios.put(
                `${this.baseApiUrl}/${campaign.id}/${endpoint}`,
            );
            if (response) {
                campaign.important = important;
            }
            return campaign;
        } catch (err) {
            this.errorStore.handleApiError(err);
            return null;
        }
    }

    public generateReport(campaign: ICampaign): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            axios
                .get(`${this.baseApiUrl}/${campaign.id}/report`, {
                    responseType: "blob",
                })
                .then((res) => {
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute(
                        "download",
                        `${campaign.name}_${DateTime.local().toFormat("dd.MM.yyyy")}.pdf`,
                    );
                    document.body.appendChild(link);
                    link.click();
                    resolve(res.data);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }
}
